/** XeAgents.ai**/
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Save, Loader2, AlertCircle, Brain, Cloud, Zap, Eye, EyeOff, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";

interface ConversationalLLMSettings {
  conversational_llm_provider: string;
  conversational_llm_model: string;
  conversational_llm_temperature: string;
  conversational_llm_max_tokens: string;
  anthropic_api_key: string;
  openai_conversational_api_key: string;
  gemini_api_key: string;
  kie_api_key: string;
}

const LLM_PROVIDERS = [
  { value: "openai", label: "OpenAI", icon: Zap },
  { value: "claude", label: "Anthropic Claude", icon: Brain },
  { value: "azure_openai", label: "Azure OpenAI", icon: Cloud },
  { value: "kie_claude", label: "Kie.ai Claude", icon: Sparkles },
];

// Updated Claude models with correct naming (as of 2026)
const LLM_MODELS = {
  claude: [
    { value: "claude-3-5-sonnet-20240620", label: "Claude 3.5 Sonnet", description: "Most capable - Recommended" },
    { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku", description: "Fast, economical" },
    { value: "claude-3-opus-20240229", label: "Claude 3 Opus", description: "Premium quality" },
    { value: "claude-3-sonnet-20240229", label: "Claude 3 Sonnet", description: "Balanced performance" },
  ],
};

export default function ConversationalLLMSettings() {
  const { toast } = useToast();
  const [formData, setFormData] = useState<Partial<ConversationalLLMSettings>>({
    conversational_llm_provider: "openai",
    conversational_llm_model: "gpt-5.6-sol",
    conversational_llm_temperature: "0.7",
    conversational_llm_max_tokens: "500",
    anthropic_api_key: "",
    openai_conversational_api_key: "",
    gemini_api_key: "",
    kie_api_key: "",
  });

  // Fetch settings
  const { data: settingsData } = useQuery<any>({ queryKey: ["/api/admin/settings"] });
  const settings = settingsData?.data || settingsData;

  // Initialize form data from settings
  useEffect(() => {
    if (settings) {
      setFormData({
        conversational_llm_provider: settings.conversational_llm_provider || "claude",
        ...(settings.conversational_llm_model
          ? { conversational_llm_model: settings.conversational_llm_model }
          : {}),
        conversational_llm_temperature: settings.conversational_llm_temperature || "0.7",
        conversational_llm_max_tokens: settings.conversational_llm_max_tokens || "500",
        anthropic_api_key: settings.anthropic_api_key || "",
        openai_conversational_api_key: settings.openai_conversational_api_key || "",
        gemini_api_key: settings.gemini_api_key || "",
        kie_api_key: settings.kie_api_key || "",
      });
    }
  }, [settings]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      return apiRequest("PATCH", `/api/admin/settings/${key}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
  });

  const handleChange = (key: keyof ConversationalLLMSettings, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const promises = [];

      // Save provider
      if (formData.conversational_llm_provider) {
        promises.push(
          updateSetting.mutateAsync({
            key: "conversational_llm_provider",
            value: formData.conversational_llm_provider,
          })
        );
      }

      // Save model
      if (formData.conversational_llm_model) {
        promises.push(
          updateSetting.mutateAsync({
            key: "conversational_llm_model",
            value: formData.conversational_llm_model,
          })
        );
      }

      // Save temperature
      if (formData.conversational_llm_temperature) {
        promises.push(
          updateSetting.mutateAsync({
            key: "conversational_llm_temperature",
            value: formData.conversational_llm_temperature,
          })
        );
      }

      // Save max tokens
      if (formData.conversational_llm_max_tokens) {
        promises.push(
          updateSetting.mutateAsync({
            key: "conversational_llm_max_tokens",
            value: formData.conversational_llm_max_tokens,
          })
        );
      }

      // Save API keys if provided and not masked
      if (
        formData.anthropic_api_key &&
        !formData.anthropic_api_key.match(/^\*+$/)
      ) {
        promises.push(
          updateSetting.mutateAsync({
            key: "anthropic_api_key",
            value: formData.anthropic_api_key,
          })
        );
      }

      if (
        formData.openai_conversational_api_key &&
        !formData.openai_conversational_api_key.match(/^\*+$/)
      ) {
        promises.push(
          updateSetting.mutateAsync({
            key: "openai_conversational_api_key",
            value: formData.openai_conversational_api_key,
          })
        );
      }

      if (
        formData.gemini_api_key &&
        !formData.gemini_api_key.match(/^\*+$/)
      ) {
        promises.push(
          updateSetting.mutateAsync({
            key: "gemini_api_key",
            value: formData.gemini_api_key,
          })
        );
      }

      if (
        formData.kie_api_key &&
        !formData.kie_api_key.match(/^\*+$/)
      ) {
        promises.push(
          updateSetting.mutateAsync({
            key: "kie_api_key",
            value: formData.kie_api_key,
          })
        );
      }

      // Auto-enable use_azure_openai when azure_openai provider is selected
      if (formData.conversational_llm_provider === "azure_openai" && !settings?.use_azure_openai) {
        promises.push(
          updateSetting.mutateAsync({
            key: "use_azure_openai",
            value: true,
          })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        toast({
          title: "Conversational LLM Settings Saved",
          description: formData.conversational_llm_provider === "azure_openai"
            ? "Azure OpenAI has been set as the default LLM provider"
            : "Default LLM configuration has been updated successfully",
        });
      } else {
        toast({
          title: "No Changes",
          description: "No settings were changed",
          variant: "default",
        });
      }
    } catch (error: any) {
      toast({
        title: "Failed to Save",
        description: error.message || "Could not save conversational LLM settings",
        variant: "destructive",
      });
    }
  };

  const [dynamicModels, setDynamicModels] = useState<
    { modelId: string; name: string; description?: string }[]
  >([]);

  // State for showing/hiding API keys
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showKieKey, setShowKieKey] = useState(false);

  const selectedProviderModels = dynamicModels;

  useEffect(() => {
    const fetchModelsForProvider = async () => {
      if (formData.conversational_llm_provider === "openai") {
        // Fetch OpenAI models dynamically from API
        try {
          const res = await apiRequest(
            "GET",
            "/api/admin/llm-models/provider/openai"
          );

          const data = await res.json();

          // Sort by model ID descending (latest first) and filter GPT models
          const filtered = (data || [])
            .filter((m: any) => m.modelId.includes('gpt'))
            .sort((a: any, b: any) => b.modelId.localeCompare(a.modelId));

          setDynamicModels(filtered);

          // Auto-select latest (first) only if no model is already saved
          if (filtered.length > 0 && (!formData.conversational_llm_model || formData.conversational_llm_model.includes("claude"))) {
            setFormData((prev) => ({
              ...prev,
              conversational_llm_model: filtered[0].modelId,
            }));
          }
        } catch (error) {
          console.error("Failed to fetch OpenAI models:", error);
          // Fallback to static list if API fails (July 2026)
          const fallbackModels = [
            { modelId: "gpt-5.6-sol", name: "GPT-5.6 Sol", description: "Strongest - Complex reasoning, coding (Recommended)" },
            { modelId: "gpt-5.6-terra", name: "GPT-5.6 Terra", description: "Balance of intelligence and cost" },
            { modelId: "gpt-5.6-luna", name: "GPT-5.6 Luna", description: "Lower-cost, high-volume workloads" },
            { modelId: "gpt-live", name: "GPT-Live", description: "Real-time voice interactions" },
            { modelId: "gpt-5.5", name: "GPT-5.5", description: "Previous flagship generation" },
            { modelId: "gpt-5.4-mini", name: "GPT-5.4 Mini", description: "Smaller, faster model" },
            { modelId: "gpt-4o", name: "GPT-4o", description: "Proven reliability" },
          ];
          setDynamicModels(fallbackModels);
          if (!formData.conversational_llm_model || formData.conversational_llm_model.includes("claude")) {
            setFormData((prev) => ({ ...prev, conversational_llm_model: "gpt-5.6-sol" }));
          }
        }
      } else if (formData.conversational_llm_provider === "claude") {
        try {
          const res = await apiRequest(
            "GET",
            "/api/admin/llm-models/provider/anthropic"
          );

          const data = await res.json();

          // Filter models with major version >= 4
          const filtered = (data || []).filter((m: any) => {
            const match = m.modelId.match(/claude-(?:sonnet-)?(\d+)/i);
            if (!match) return false;
            const version = parseInt(match[1], 10);
            return version >= 4;
          });

          // Sort descending by modelId (latest first)
          filtered.sort((a: any, b: any) =>
            b.modelId.localeCompare(a.modelId)
          );

          setDynamicModels(filtered);

          // Auto-select latest (first) only if no model is already saved
          if (filtered.length > 0 && !formData.conversational_llm_model) {
            setFormData((prev) => ({
              ...prev,
              conversational_llm_model: filtered[0].modelId,
            }));
          }
        } catch (error) {
          console.error("Failed to fetch Claude models:", error);
        }
      } else if (formData.conversational_llm_provider === "azure_openai") {
        // For Azure OpenAI, use the deployment name from global settings
        const deploymentName = settings?.azure_openai_deployment || "gpt-5.2-chat";
        setDynamicModels([
          {
            modelId: deploymentName,
            name: `${deploymentName} (Azure Deployment)`,
            description: "Uses your Azure OpenAI deployment configured above",
          },
        ]);
        // Auto-select the deployment as model
        if (!formData.conversational_llm_model || formData.conversational_llm_model.includes("claude")) {
          setFormData((prev) => ({
            ...prev,
            conversational_llm_model: deploymentName,
          }));
        }
      } else if (formData.conversational_llm_provider === "kie_claude") {
        // Kie.ai Claude models - static list
        const kieModels = [
          {
            modelId: "claude-sonnet-4-6",
            name: "Claude Sonnet 4.6 (Latest)",
            description: "Most capable model with excellent reasoning",
          },
          {
            modelId: "claude-sonnet-4-20250514",
            name: "Claude Sonnet 4",
            description: "High capability via Kie.ai",
          },
          {
            modelId: "claude-3-5-sonnet-20241022",
            name: "Claude 3.5 Sonnet",
            description: "Previous generation via Kie.ai",
          },
        ];

        setDynamicModels(kieModels);

        // Auto-select claude-sonnet-4-6 if no model is already saved
        if (!formData.conversational_llm_model || !formData.conversational_llm_model.includes("claude-sonnet-4")) {
          setFormData((prev) => ({
            ...prev,
            conversational_llm_model: "claude-sonnet-4-6",
          }));
        }
      } else {
        setDynamicModels([]);
      }
    };

    fetchModelsForProvider();
  }, [formData.conversational_llm_provider, settings?.azure_openai_deployment]);

  return (
    <Card id="conversational-llm-settings" className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          Default LLM Model for All Users
        </CardTitle>
        <CardDescription>
          Configure the default language model for conversational AI agents (Bulbul/Plivo). This LLM will be used by all agents and cannot be overridden.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Important:</strong> This is a global setting. All agents using the conversational LLM (e.g., Bulbul/Plivo agents) will use this configured model.
          </AlertDescription>
        </Alert>

        {/* LLM Provider Selection */}
        <div className="space-y-2">
          <div className="flex items-center">
            <Label>LLM Provider</Label>
            <InfoTooltip content="Select the LLM provider for conversational AI" />
          </div>
          <Select
            value={formData.conversational_llm_provider}
            onValueChange={(value) => {
              handleChange("conversational_llm_provider", value);
              // Model will be auto-selected dynamically via useEffect
            }}
          >
            <SelectTrigger data-testid="select-conversational-llm-provider">
              <SelectValue placeholder="Select LLM provider..." />
            </SelectTrigger>
            <SelectContent>
              {LLM_PROVIDERS.filter((p) => !["kie_claude", "azure_openai"].includes(p.value) || formData.conversational_llm_provider === p.value).map((provider) => (
                <SelectItem key={provider.value} value={provider.value}>
                  <div className="flex items-center gap-2">
                    <provider.icon className="h-4 w-4" />
                    {provider.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* OpenAI info alert */}
        {formData.conversational_llm_provider === "openai" && (
          <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
            <Zap className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-sm text-green-800 dark:text-green-200">
              <strong>OpenAI:</strong> Configure your OpenAI API key in the &quot;API Keys&quot; section below. GPT-5.6 Sol is recommended for complex flow generation with excellent structured output capabilities.
              {!formData.openai_conversational_api_key && (
                <span className="block mt-1 text-orange-600 dark:text-orange-400 font-medium">
                  ⚠️ OpenAI API key not configured yet. Please enter your API key below.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Azure OpenAI info alert */}
        {formData.conversational_llm_provider === "azure_openai" && (
          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <Cloud className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Azure OpenAI:</strong> API Key, Endpoint, Deployment Name, and API Version are configured in the <strong>&quot;Azure OpenAI Configuration&quot;</strong> section above. The deployment name ({settings?.azure_openai_deployment || "not set"}) will be used as the model.
              {!settings?.azure_openai_configured && (
                <span className="block mt-1 text-orange-600 dark:text-orange-400 font-medium">
                  ⚠️ Azure OpenAI is not yet configured. Please scroll up and configure your Azure OpenAI credentials first.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Kie.ai Claude info alert */}
        {formData.conversational_llm_provider === "kie_claude" && (
          <Alert className="bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
            <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <AlertDescription className="text-sm text-purple-800 dark:text-purple-200">
              <strong>Kie.ai Claude:</strong> Access Claude Sonnet 4.6 via Kie.ai API aggregator. Configure your Kie API key in the &quot;API Keys&quot; section below.
              {!formData.kie_api_key && (
                <span className="block mt-1 text-orange-600 dark:text-orange-400 font-medium">
                  ⚠️ Kie API key not configured yet. Please enter your API key below.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* LLM Model Selection */}
        {formData.conversational_llm_provider && (
          <div className="space-y-2">
            <div className="flex items-center">
              <Label>LLM Model</Label>
              <InfoTooltip content="Select the specific model to use" />
            </div>
            <Select
              value={formData.conversational_llm_model}
              onValueChange={(value) => handleChange("conversational_llm_model", value)}
            >
              <SelectTrigger data-testid="select-conversational-llm-model">
                <SelectValue placeholder="Select model..." />
              </SelectTrigger>
              <SelectContent>
                {selectedProviderModels.map((model) => (
                  <SelectItem key={model.modelId} value={model.modelId}>
                    <div className="flex flex-col">
                      <span className="font-medium">{model.name}</span>
                      {model.description && (
                        <span className="text-xs text-muted-foreground">
                          {model.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* API Keys Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
          <h4 className="font-semibold text-sm">API Keys</h4>

          {/* Anthropic API Key */}
          <div>
            <div className="flex items-center">
              <Label>Anthropic API Key (for Claude)</Label>
              <InfoTooltip content="API key for Claude models" />
            </div>
            <div className="relative">
              <Input
                type={showAnthropicKey ? "text" : "password"}
                value={formData.anthropic_api_key}
                onChange={(e) => handleChange("anthropic_api_key", e.target.value)}
                placeholder="sk-ant-..."
                data-testid="input-anthropic-api-key"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showAnthropicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="underline">Anthropic Console</a>
            </p>
          </div>

          {/* OpenAI API Key */}
          <div>
            <div className="flex items-center">
              <Label>OpenAI API Key (for GPT)</Label>
              <InfoTooltip content="API key for OpenAI GPT models (separate from Azure)" />
            </div>
            <div className="relative">
              <Input
                type={showOpenAIKey ? "text" : "password"}
                value={formData.openai_conversational_api_key}
                onChange={(e) => handleChange("openai_conversational_api_key", e.target.value)}
                placeholder="sk-..."
                data-testid="input-openai-conversational-api-key"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showOpenAIKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">OpenAI Platform</a>
            </p>
          </div>

          {/* Kie.ai API Key: shown only while Kie is the selected provider */}
          {formData.conversational_llm_provider === "kie_claude" && (
          <div>
            <div className="flex items-center">
              <Label>Kie.ai API Key (for Claude via Kie)</Label>
              <InfoTooltip content="API key for accessing Claude models via Kie.ai aggregator" />
            </div>
            <div className="relative">
              <Input
                type={showKieKey ? "text" : "password"}
                value={formData.kie_api_key}
                onChange={(e) => handleChange("kie_api_key", e.target.value)}
                placeholder="kie_..."
                data-testid="input-kie-api-key"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowKieKey(!showKieKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showKieKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://kie.ai" target="_blank" rel="noopener noreferrer" className="underline">Kie.ai Dashboard</a>
            </p>
          </div>
          )}
        </div>

        {/* Advanced Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center">
              <Label>Temperature</Label>
              <InfoTooltip content="Controls randomness (0.0-2.0). Lower = more focused" />
            </div>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={formData.conversational_llm_temperature}
              onChange={(e) => handleChange("conversational_llm_temperature", e.target.value)}
              data-testid="input-conversational-llm-temperature"
            />
          </div>

          <div>
            <div className="flex items-center">
              <Label>Max Tokens</Label>
              <InfoTooltip content="Maximum tokens in LLM response" />
            </div>
            <Input
              type="number"
              min="1"
              max="4000"
              value={formData.conversational_llm_max_tokens}
              onChange={(e) => handleChange("conversational_llm_max_tokens", e.target.value)}
              data-testid="input-conversational-llm-max-tokens"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={updateSetting.isPending}
            data-testid="button-save-conversational-llm"
          >
            {updateSetting.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Conversational LLM Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}